/**
 * Player Help Center — searchable, category-filtered, accordion topics.
 * Topics come from /api/help/topics (admin-editable).
 *
 * Deep-link support: visit `/help#combo-bets` and the matching topic
 * scrolls into view and auto-expands.
 */
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as Icons from 'lucide-react';
import { Input } from '../components/ui/input';
import {
    Loader2, Search, ChevronDown, LifeBuoy, ArrowLeft, Lightbulb,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const renderMarkdown = (md) => {
    // Tiny markdown subset: **bold**, • bullets, blank-line paragraphs.
    if (!md) return null;
    const blocks = md.split(/\n\n+/);
    return blocks.map((block, i) => {
        const lines = block.split('\n');
        const isList = lines.every(l => /^\s*[•\-]\s/.test(l) || /^\s*\d+\.\s/.test(l));
        const formatInline = (text) => {
            const parts = text.split(/(\*\*[^*]+\*\*)/);
            return parts.map((p, j) =>
                p.startsWith('**') && p.endsWith('**')
                    ? <strong key={j} className="text-zinc-100">{p.slice(2, -2)}</strong>
                    : <span key={j}>{p}</span>
            );
        };
        if (isList) {
            return (
                <ul key={i} className="space-y-1 pl-2 my-3">
                    {lines.map((l, j) => (
                        <li key={j} className="text-zinc-300 text-sm leading-relaxed">
                            {formatInline(l.replace(/^\s*[•\-]\s/, '').replace(/^\s*\d+\.\s/, ''))}
                        </li>
                    ))}
                </ul>
            );
        }
        return <p key={i} className="text-zinc-300 text-sm leading-relaxed my-3">{formatInline(block)}</p>;
    });
};

const TopicCard = ({ topic, defaultOpen }) => {
    const [open, setOpen] = useState(defaultOpen);
    useEffect(() => { setOpen(defaultOpen); }, [defaultOpen]);
    const Icon = Icons[topic.icon] || Icons.HelpCircle;
    return (
        <div id={`topic-${topic.slug}`}
            className="rounded-md border border-zinc-800 bg-zinc-900/40 overflow-hidden mb-2"
            data-testid={`help-topic-${topic.slug}`}>
            <button onClick={() => setOpen(o => !o)}
                className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-zinc-900"
                data-testid={`help-topic-toggle-${topic.slug}`}>
                <div className="w-9 h-9 rounded bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-emerald-300" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-white font-bold text-base truncate">{topic.title}</p>
                    <p className="text-xs text-zinc-500 truncate">{topic.short}</p>
                </div>
                <ChevronDown className={`w-4 h-4 text-zinc-500 transition flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
                <div className="px-4 pb-4 border-t border-zinc-800 bg-zinc-950/40">
                    {renderMarkdown(topic.body_md)}
                    {topic.pro_tip && (
                        <div className="mt-3 rounded border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-2">
                            <Lightbulb className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
                            <p className="text-amber-100 text-xs leading-relaxed">
                                <span className="font-bold text-amber-300">Pro tip · </span>
                                {topic.pro_tip}
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default function Help() {
    const [topics, setTopics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [category, setCategory] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();
    const hashSlug = (location.hash || '').replace(/^#/, '');

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/help/topics`);
                if (mounted) setTopics(data || []);
            } catch { /* ignore */ }
            finally { if (mounted) setLoading(false); }
        })();
        return () => { mounted = false; };
    }, []);

    // Scroll to deep-link target after topics load
    useEffect(() => {
        if (!hashSlug || loading) return;
        const t = setTimeout(() => {
            const el = document.getElementById(`topic-${hashSlug}`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
        return () => clearTimeout(t);
    }, [hashSlug, loading]);

    const categories = useMemo(() => {
        const seen = new Set();
        const out = [];
        topics.forEach(t => { if (!seen.has(t.category)) { seen.add(t.category); out.push(t.category); } });
        return out;
    }, [topics]);

    const filtered = useMemo(() => {
        return topics.filter(t => {
            if (category && t.category !== category) return false;
            if (q.trim()) {
                const needle = q.trim().toLowerCase();
                return (t.title + ' ' + t.short + ' ' + t.body_md).toLowerCase().includes(needle);
            }
            return true;
        });
    }, [topics, q, category]);

    const grouped = useMemo(() => {
        const out = {};
        filtered.forEach(t => {
            if (!out[t.category]) out[t.category] = [];
            out[t.category].push(t);
        });
        return out;
    }, [filtered]);

    return (
        <div className="min-h-screen bg-zinc-950 px-4 py-6 pb-24">
            <div className="max-w-3xl mx-auto">
                <div className="flex items-center gap-3 mb-6">
                    <button onClick={() => navigate(-1)}
                        className="w-9 h-9 rounded-md border border-zinc-800 bg-zinc-900 text-zinc-400 hover:text-zinc-100 flex items-center justify-center"
                        data-testid="help-back">
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                            <LifeBuoy className="w-6 h-6 text-emerald-400" />
                            Help Center
                        </h1>
                        <p className="text-xs text-zinc-500 mt-1">
                            Tap any topic to learn how it works. Stuck? Reach out to support.
                        </p>
                    </div>
                </div>

                <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)}
                        placeholder="Search help — e.g. combo, free play, cashout…"
                        className="pl-10 bg-zinc-900 border-zinc-800 text-white h-11"
                        data-testid="help-search" />
                </div>

                {categories.length > 1 && (
                    <div className="flex gap-2 overflow-x-auto pb-2 mb-2 -mx-1 px-1" data-testid="help-categories">
                        <button onClick={() => setCategory(null)}
                            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs border ${
                                !category ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-200' : 'border-zinc-800 bg-zinc-900 text-zinc-400 hover:text-zinc-200'
                            }`}>All</button>
                        {categories.map(c => (
                            <button key={c} onClick={() => setCategory(c)}
                                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs border ${
                                    category === c ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-200' : 'border-zinc-800 bg-zinc-900 text-zinc-400 hover:text-zinc-200'
                                }`}>{c}</button>
                        ))}
                    </div>
                )}

                {loading ? (
                    <div className="text-center py-20 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-12 text-zinc-500 text-sm">No topics match that search.</div>
                ) : (
                    Object.entries(grouped).map(([cat, items]) => (
                        <div key={cat} className="mb-5">
                            <h2 className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2 px-1">{cat}</h2>
                            {items.map(t => (
                                <TopicCard key={t.slug} topic={t}
                                    defaultOpen={t.slug === hashSlug || (q.trim() && filtered.length <= 3)} />
                            ))}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}
